import type {
  Customer,
  Label,
  LeadInsuranceType,
  LeadGroupSummary,
  PipelineHistoryResponse,
  PolicyEditablePayload,
  TenderTemplate,
  TenderAutofillMode,
  TenderWorkspaceResponse,
  WorkListItem,
  WorkListSaveJob,
  WorkListSummary
} from './types';
import type {
  LeadOffer,
  LeadOfferUpdatePayload,
  NeedsAnalysisResponse,
  NeedsAnalysisUpdatePayload
} from './needsAnalysisTypes';

function resolveBaseUrl() {
  const envUrl = (process.env.REACT_APP_API_URL || '').trim();
  if (envUrl) {
    try {
      const parsed = new URL(envUrl);
      // If the site is served over HTTPS but the configured API URL is HTTP to the same host,
      // upgrade to HTTPS to avoid mixed-content/CSP issues.
      if (typeof window !== 'undefined' && window.location.protocol === 'https:' && parsed.protocol === 'http:') {
        parsed.protocol = 'https:';
        return parsed.origin.replace(/\/$/, '');
      }
      return envUrl.replace(/\/$/, '');
    } catch (_err) {
      return envUrl.replace(/\/$/, '');
    }
  }

  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  return '';
}

const baseUrl = resolveBaseUrl();
let access = '';
let refresh: string | null = null;
let unauthorizedHandler: (() => void) | null = null;
let tokenListener: ((access: string, refresh: string | null) => void) | null = null;
let refreshPromise: Promise<boolean> | null = null;

function notifyTokenChange() {
  if (tokenListener) {
    tokenListener(access, refresh);
  }
}

export function setTokens(accessToken: string, refreshToken?: string | null) {
  access = accessToken || '';
  if (typeof refreshToken !== 'undefined') {
    refresh = refreshToken ? refreshToken : null;
  }
  notifyTokenChange();
}

export function setToken(t: string) {
  setTokens(t);
}

export function clearTokens() {
  access = '';
  refresh = null;
  notifyTokenChange();
}

export function setTokenListener(listener: ((access: string, refresh: string | null) => void) | null) {
  tokenListener = listener;
  if (listener) {
    listener(access, refresh);
  }
}

export function setUnauthorizedHandler(handler: (() => void) | null) {
  unauthorizedHandler = handler;
}

export function getAccessToken() {
  return access;
}

export async function refreshAccessToken() {
  const refreshed = await requestRefresh();
  return refreshed ? access : null;
}

async function requestRefresh(): Promise<boolean> {
  if (!refresh) {
    return false;
  }

  if (!refreshPromise) {
    const refreshPath = '/api/auth/refresh';
    const refreshUrl = baseUrl ? `${baseUrl}${refreshPath}` : refreshPath;
    const payload = JSON.stringify({ token: refresh });
    const headers = new Headers({ 'Content-Type': 'application/json' });

    refreshPromise = fetch(refreshUrl, {
      method: 'POST',
      headers,
      body: payload
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('refresh failed');
        }
        const data = await response.json().catch(() => ({}));
        const newAccess = typeof data.access === 'string' ? data.access : '';
        if (!newAccess) {
          throw new Error('missing access token');
        }
        setTokens(newAccess);
        return true;
      })
      .catch(() => {
        clearTokens();
        return false;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  try {
    return await refreshPromise;
  } catch {
    return false;
  }
}

export async function api(path: string, init: RequestInit = {}) {
  const headers = new Headers(init.headers || {});
  if (access) headers.set('Authorization', `Bearer ${access}`);

  const isAbsolute = /^https?:\/\//i.test(path);
  const url = isAbsolute ? path : `${baseUrl}${path}`;
  const isRefreshEndpoint = /\/api\/auth\/refresh$/.test(url);

  async function performRequest(allowRetry: boolean): Promise<Response> {
    const attemptHeaders = new Headers(init.headers || {});
    if (access) {
      attemptHeaders.set('Authorization', `Bearer ${access}`);
    }
    const response = await fetch(url, { ...init, headers: attemptHeaders });

    if (response.status !== 401) {
      return response;
    }

    if (!allowRetry || isRefreshEndpoint) {
      if (unauthorizedHandler) {
        unauthorizedHandler();
      }
      return response;
    }

    const refreshed = await requestRefresh();
    if (!refreshed) {
      if (unauthorizedHandler) {
        unauthorizedHandler();
      }
      return response;
    }

    return performRequest(false);
  }

  return performRequest(true);
}

export async function fetchLeadGroups(): Promise<LeadGroupSummary[]> {
  const response = await api('/api/lead-groups');
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke hente samlekunder');
  }
  const groups = Array.isArray((data as any)?.groups) ? (data as any).groups : [];
  return groups as LeadGroupSummary[];
}

export async function fetchPipelineHistory(days: number): Promise<PipelineHistoryResponse> {
  const normalizedDays = [7, 14, 30].includes(days) ? days : 30;
  const params = new URLSearchParams({ days: String(normalizedDays) });
  const response = await api(`/api/leads/pipeline-history?${params.toString()}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke hente pipelinehistorik');
  }
  const payload = data as PipelineHistoryResponse;
  return {
    ...payload,
    points: Array.isArray(payload.points) ? payload.points : []
  };
}

type CreateCustomerDraftPayload = {
  company_name: string;
  cvrnummer: string;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  primary_contact_phone?: string | null;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  industry?: string | null;
  annual_value?: number | null;
  commission_percent?: number | null;
  commission_amount?: number | null;
  currency?: string | null;
  contract_signed_at?: string | null;
  contract_starts_at?: string | null;
  contract_expires_at?: string | null;
  renewal_notice_at?: string | null;
  notes?: string | null;
  cvr_links?: Array<{
    cvrnummer: string;
    annual_value?: number | null;
    commission_percent?: number | null;
    commission_amount?: number | null;
    currency?: string | null;
    contract_signed_at?: string | null;
    contract_starts_at?: string | null;
    contract_expires_at?: string | null;
    renewal_notice_at?: string | null;
    notes?: string | null;
  }>;
};

export const createCustomerDraftFromLead = async (leadId: string | number, payload: CreateCustomerDraftPayload) => {
  const response = await api(`/api/customers/from-lead/${leadId}/draft`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke oprette kunde-kladde');
  }
  return data as Customer;
};

export async function fetchLabels(): Promise<Label[]> {
  const response = await api('/api/labels');
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke hente labels');
  }
  const items = Array.isArray((data as any)?.items ?? data) ? (data as any).items ?? data : [];
  return items as Label[];
}

type CreateCustomerPayload = {
  company_name: string;
  cvrnummer?: string | null;
  label_ids?: string[];
  lead_id?: string | null;
  account_owner?: string | null;
  annual_value?: number | null;
  commission_percent?: number | null;
  commission_amount?: number | null;
  currency?: string | null;
  contract_signed_at?: string | null;
  contract_starts_at?: string | null;
  contract_expires_at?: string | null;
  renewal_notice_at?: string | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  primary_contact_phone?: string | null;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  industry?: string | null;
  status?: string | null;
  notes?: string | null;
};

export async function createCustomer(payload: CreateCustomerPayload): Promise<Customer> {
  const response = await api('/api/customers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke oprette kunde');
  }
  return data as Customer;
}

type EditablePolicyCopyResponse = {
  edit: {
    id: string;
    policy_document_id: string;
    lead_id: string;
    summary: string | null;
    payload: PolicyEditablePayload;
    created_by: string | null;
    updated_by: string | null;
    created_at: string;
    updated_at: string;
  } | null;
  effective_analysis_payload?: Record<string, unknown> | null;
  effective_analysis_summary?: string | null;
};

type PolicyInsuranceTypesResponse = {
  items: LeadInsuranceType[];
  editableCopyCreated?: boolean;
  editableCopyUpdated?: boolean;
};

export type RelevantTenderTemplatesResponse = {
  policyInsuranceTypes: Array<{ id: number; title: string }>;
  templates: TenderTemplate[];
  selectedTemplateId: string | null;
};

export type PolicyExportType = 'tender' | 'customer-agreement';

export async function fetchTenderWorkspace(leadId: string, policyId: string): Promise<TenderWorkspaceResponse> {
  const response = await api(`/api/leads/${leadId}/tender?policy_id=${encodeURIComponent(policyId)}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke hente udbudsværksted');
  }
  return data as TenderWorkspaceResponse;
}

export async function queueTenderWorkspace(
  leadId: string,
  policyId: string,
  autofillMode: TenderAutofillMode = 'STANDARD'
): Promise<void> {
  const response = await api(`/api/leads/${leadId}/tender`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ policy_id: policyId, autofill_mode: autofillMode })
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke starte udbudsbehandling');
  }
}

export async function fillTenderMissingCoverageNegative(leadId: string, policyId: string): Promise<{ updated_fields: number }> {
  const response = await api(`/api/leads/${leadId}/tender/fill-missing-negative`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ policy_id: policyId })
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke autoudfylde felter uden dækning');
  }
  return data as { updated_fields: number };
}

export async function createEditableTenderRevisionCopy(leadId: string, policyId: string): Promise<void> {
  const response = await api(`/api/leads/${leadId}/tender/editable-copy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ policy_id: policyId })
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke oprette redigerbar revisionskopi');
  }
}

export async function updateTenderField(leadId: string, fieldId: string, value: unknown): Promise<void> {
  const response = await api(`/api/leads/${leadId}/tender/fields/${fieldId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ value })
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke opdatere udbudsfelt');
  }
}

export async function answerTenderQuestion(
  leadId: string,
  questionId: string,
  answer: string,
  submitAsKnowledge: boolean
): Promise<void> {
  const response = await api(`/api/leads/${leadId}/tender/questions/${questionId}/answers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answer, submitAsKnowledge })
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke gemme afklaringssvar');
  }
}

export async function decideTenderKnowledge(
  leadId: string,
  knowledgeId: string,
  decision: 'APPROVED' | 'REJECTED'
): Promise<void> {
  const response = await api(`/api/leads/${leadId}/tender/knowledge/${knowledgeId}/decision`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ decision })
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke behandle vidensforslaget');
  }
}

export async function approveTenderRevision(leadId: string, revisionId: string): Promise<void> {
  const response = await api(`/api/leads/${leadId}/tender/revisions/${revisionId}/approve`, {
    method: 'POST'
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const blockerCount = Array.isArray((data as any)?.blockers) ? (data as any).blockers.length : 0;
    const suffix = blockerCount ? ` (${blockerCount} åbne)` : '';
    throw new Error(`${(data as any)?.error || 'Kunne ikke godkende udbudsrevision'}${suffix}`);
  }
}

export async function createEditablePolicyCopy(leadId: string, policyId: string): Promise<EditablePolicyCopyResponse> {
  const response = await api(`/api/leads/${leadId}/policies/${policyId}/editable-copy`, {
    method: 'POST'
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke oprette redigerbar kopi');
  }
  return data as EditablePolicyCopyResponse;
}

export async function updateEditablePolicyCopy(
  leadId: string,
  policyId: string,
  payload: { summary: string | null; payload: PolicyEditablePayload }
): Promise<EditablePolicyCopyResponse> {
  const response = await api(`/api/leads/${leadId}/policies/${policyId}/editable-copy`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke gemme redigerbar kopi');
  }
  return data as EditablePolicyCopyResponse;
}

export async function downloadPolicyAnalysisPdf(
  leadId: string,
  policyId: string,
  type: PolicyExportType
): Promise<Blob> {
  const query = new URLSearchParams({ type });
  const response = await api(`/api/leads/${leadId}/policies/${policyId}/export-pdf?${query.toString()}`);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error((data as any)?.error || 'Kunne ikke generere PDF');
  }
  return response.blob();
}

export async function downloadCombinedPoliciesPdf(
  leadId: string,
  type: PolicyExportType
): Promise<Blob> {
  const query = new URLSearchParams({ type });
  const response = await api(`/api/leads/${leadId}/policies/export-pdf-combined?${query.toString()}`);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error((data as any)?.error || 'Kunne ikke generere samlet PDF');
  }
  return response.blob();
}

export async function downloadInsuranceConditionCustomerReportPdf(payload: {
  sourceDocumentId: string;
  targetDocumentId: string;
  areaCode?: string | null;
  limit?: number;
}): Promise<Blob> {
  const response = await api('/api/insurance-conditions/match/customer-report/pdf', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error((data as any)?.error || 'Kunne ikke generere kunderesume som PDF');
  }
  return response.blob();
}

async function readJson<T>(response: Response, fallbackError: string): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || fallbackError);
  }
  return data as T;
}

export async function fetchNeedsAnalysis(leadId: string): Promise<NeedsAnalysisResponse> {
  return readJson(await api(`/api/leads/${leadId}/needs-analysis`), 'Kunne ikke hente behovsanalysen');
}

export async function updateNeedsAnalysis(
  leadId: string,
  payload: NeedsAnalysisUpdatePayload
): Promise<NeedsAnalysisResponse> {
  const response = await api(`/api/leads/${leadId}/needs-analysis`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return readJson(response, 'Kunne ikke gemme behovsanalysen');
}

export async function applyNeedsAnalysisAiSuggestions(leadId: string): Promise<{
  updated_sections: number;
  updated_items: number;
  payload: NeedsAnalysisResponse;
}> {
  const response = await api(`/api/leads/${leadId}/needs-analysis/apply-ai-suggestions`, { method: 'POST' });
  return readJson(response, 'Kunne ikke hente forslag fra AI-analysen');
}

export async function createNeedsAnalysisCustomItem(
  leadId: string,
  payload: { section_id: number; label: string; answer_type: string }
): Promise<{ id: string }> {
  const response = await api(`/api/leads/${leadId}/needs-analysis/custom-items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return readJson(response, 'Kunne ikke tilføje punktet');
}

export async function deleteNeedsAnalysisCustomItem(leadId: string, itemId: string): Promise<void> {
  const response = await api(`/api/leads/${leadId}/needs-analysis/custom-items/${itemId}`, { method: 'DELETE' });
  await readJson(response, 'Kunne ikke slette punktet');
}

export async function approveNeedsAnalysis(leadId: string): Promise<NeedsAnalysisResponse> {
  const response = await api(`/api/leads/${leadId}/needs-analysis/approve`, { method: 'POST' });
  return readJson(response, 'Kunne ikke godkende behovsanalysen');
}

export async function reopenNeedsAnalysis(leadId: string): Promise<NeedsAnalysisResponse> {
  const response = await api(`/api/leads/${leadId}/needs-analysis/reopen`, { method: 'POST' });
  return readJson(response, 'Kunne ikke genåbne behovsanalysen');
}

export async function downloadNeedsAnalysisPdf(leadId: string): Promise<Blob> {
  const response = await api(`/api/leads/${leadId}/needs-analysis/export-pdf`);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error((data as any)?.error || 'Kunne ikke generere behovsanalysen som PDF');
  }
  return response.blob();
}

export async function fetchLeadOffers(leadId: string): Promise<{ offers: LeadOffer[] }> {
  return readJson(await api(`/api/leads/${leadId}/offers`), 'Kunne ikke hente tilbud');
}

export async function uploadLeadOffer(leadId: string, file: File): Promise<LeadOffer> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api(`/api/leads/${leadId}/offers`, { method: 'POST', body: formData });
  return readJson(response, 'Kunne ikke uploade tilbuddet');
}

export async function updateLeadOffer(
  leadId: string,
  offerId: string,
  payload: LeadOfferUpdatePayload
): Promise<LeadOffer> {
  const response = await api(`/api/leads/${leadId}/offers/${offerId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return readJson(response, 'Kunne ikke opdatere tilbuddet');
}

export async function reanalyzeLeadOffer(leadId: string, offerId: string): Promise<LeadOffer> {
  const response = await api(`/api/leads/${leadId}/offers/${offerId}/reanalyze`, { method: 'POST' });
  return readJson(response, 'Kunne ikke køre ny AI analyse');
}

export async function deleteLeadOffer(leadId: string, offerId: string): Promise<void> {
  const response = await api(`/api/leads/${leadId}/offers/${offerId}`, { method: 'DELETE' });
  await readJson(response, 'Kunne ikke slette tilbuddet');
}

export async function downloadLeadOfferPdf(leadId: string, offerId: string): Promise<Blob> {
  const response = await api(`/api/leads/${leadId}/offers/${offerId}/export-pdf`);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error((data as any)?.error || 'Kunne ikke generere tilbuddet som PDF');
  }
  return response.blob();
}

export async function downloadCombinedPoliciesExcel(leadId: string): Promise<Blob> {
  const response = await api(`/api/leads/${leadId}/policies/export-excel-combined`);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error((data as any)?.error || 'Kunne ikke generere samlet Excel');
  }
  return response.blob();
}

export async function downloadTenderApprovedRevisionExcel(leadId: string, policyId: string): Promise<Blob> {
  const response = await api(`/api/leads/${leadId}/tender/export-excel?policy_id=${encodeURIComponent(policyId)}`);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error((data as any)?.error || 'Kunne ikke eksportere godkendt udbudsmateriale');
  }
  return response.blob();
}

export async function analyzeAllPolicyDocuments(
  leadId: string,
  includeCompleted = false,
  asyncMode = false
): Promise<{
  ok: boolean;
  accepted?: boolean;
  include_completed?: boolean;
  total_documents: number;
  skipped_completed: number;
  analyzed_documents: number;
  analyzed_completed: number;
  analyzed_failed: number;
}> {
  const response = await api(`/api/leads/${leadId}/policies/analyze-all`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ include_completed: includeCompleted, async: asyncMode })
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke analysere alle policer');
  }
  return data as {
    ok: boolean;
    accepted?: boolean;
    include_completed?: boolean;
    total_documents: number;
    skipped_completed: number;
    analyzed_documents: number;
    analyzed_completed: number;
    analyzed_failed: number;
  };
}

export async function reanalyzePolicyDocument(leadId: string, policyId: string): Promise<{ ok: boolean }> {
  const response = await api(`/api/leads/${leadId}/policies/${policyId}/reanalyze`, {
    method: 'POST'
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke køre ny AI analyse');
  }
  return data as { ok: boolean };
}

export async function deletePolicyDocument(leadId: string, policyId: string): Promise<{ ok: boolean }> {
  const response = await api(`/api/leads/${leadId}/policies/${policyId}`, {
    method: 'DELETE'
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke slette policedokument');
  }
  return data as { ok: boolean };
}

export async function fetchPolicyInsuranceTypes(
  leadId: string,
  policyId: string
): Promise<PolicyInsuranceTypesResponse> {
  const response = await api(`/api/leads/${leadId}/policies/${policyId}/insurance-types`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke hente forsikringstyper for police');
  }
  return data as PolicyInsuranceTypesResponse;
}

export async function replacePolicyInsuranceTypes(
  leadId: string,
  policyId: string,
  insuranceTypeIds: string[]
): Promise<PolicyInsuranceTypesResponse> {
  const response = await api(`/api/leads/${leadId}/policies/${policyId}/insurance-types`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ insuranceTypeIds })
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke opdatere forsikringstyper for police');
  }
  return data as PolicyInsuranceTypesResponse;
}

export async function fetchRelevantTenderTemplates(
  leadId: string,
  policyId: string
): Promise<RelevantTenderTemplatesResponse> {
  const response = await api(`/api/leads/${leadId}/policies/${policyId}/tender-templates`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke hente relevante udbudsskabeloner');
  }
  return data as RelevantTenderTemplatesResponse;
}

export async function selectTenderTemplateForPolicy(
  leadId: string,
  policyId: string,
  templateId: string
): Promise<void> {
  const response = await api(`/api/leads/${leadId}/policies/${policyId}/tender-template`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ templateId })
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error((data as any)?.error || 'Kunne ikke vælge udbudsskabelon');
  }
}

export async function createWorkList(payload: {
  display_name?: string;
  work_list_id?: string;
  search_criteria: Record<string, unknown>;
}): Promise<WorkListSummary> {
  const response = await api('/api/work-lists', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke oprette arbejdsliste');
  }
  return data as WorkListSummary;
}

export async function ensureRentalPropertyOwnersWorkList(): Promise<{
  work_list: WorkListSummary;
  created: boolean;
  inserted: number;
  updated: number;
  deleted: number;
  total: number;
}> {
  const response = await api('/api/work-lists/managed/rental-property-owners', {
    method: 'POST'
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke oprette arbejdsliste for udlejningsejendomme');
  }
  return data as {
    work_list: WorkListSummary;
    created: boolean;
    inserted: number;
    updated: number;
    deleted: number;
    total: number;
  };
}

export async function createWorkListSaveJob(payload: {
  display_name?: string;
  work_list_id?: string;
  search_criteria: Record<string, unknown>;
}): Promise<{ work_list: WorkListSummary; job: WorkListSaveJob }> {
  const response = await api('/api/work-lists/save-jobs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke starte gem-job');
  }
  return {
    work_list: (data as any).work_list as WorkListSummary,
    job: (data as any).job as WorkListSaveJob
  };
}

export async function fetchWorkListSaveJob(jobId: string): Promise<WorkListSaveJob> {
  const response = await api(`/api/work-lists/save-jobs/${encodeURIComponent(jobId)}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke hente gem-job');
  }
  return (data as any).job as WorkListSaveJob;
}

export async function fetchWorkLists(params?: {
  q?: string;
  sort?: 'created_at' | 'name';
  order?: 'asc' | 'desc';
}): Promise<WorkListSummary[]> {
  const query = new URLSearchParams();
  if (params?.q) {
    query.set('q', params.q);
  }
  if (params?.sort) {
    query.set('sort', params.sort);
  }
  if (params?.order) {
    query.set('order', params.order);
  }
  const response = await api(`/api/work-lists${query.toString() ? `?${query.toString()}` : ''}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke hente arbejdslister');
  }
  return Array.isArray((data as any)?.items) ? ((data as any).items as WorkListSummary[]) : [];
}

export async function fetchWorkListDetail(
  workListId: string,
  params?: {
    q?: string;
    sort?: 'company_name' | 'cvr';
    order?: 'asc' | 'desc';
    page?: number;
    limit?: number;
    changeFilter?: 'ALL' | 'NEW' | 'UPDATED' | 'CHANGED';
    emailSent?: 'ALL' | 'YES' | 'NO';
    alreadyCustomer?: 'ALL' | 'YES' | 'NO';
    ignored?: 'ALL' | 'YES' | 'NO';
  }
): Promise<{ work_list: WorkListSummary; items: WorkListItem[]; total: number; page: number; limit: number }> {
  const query = new URLSearchParams();
  if (params?.q) {
    query.set('q', params.q);
  }
  if (params?.sort) {
    query.set('sort', params.sort);
  }
  if (params?.order) {
    query.set('order', params.order);
  }
  if (params?.page) {
    query.set('page', String(params.page));
  }
  if (params?.limit) {
    query.set('limit', String(params.limit));
  }
  if (params?.changeFilter && params.changeFilter !== 'ALL') {
    query.set('changeFilter', params.changeFilter);
  }
  if (params?.emailSent && params.emailSent !== 'ALL') {
    query.set('emailSent', params.emailSent === 'YES' ? 'true' : 'false');
  }
  if (params?.alreadyCustomer && params.alreadyCustomer !== 'ALL') {
    query.set('alreadyCustomer', params.alreadyCustomer === 'YES' ? 'true' : 'false');
  }
  if (params?.ignored && params.ignored !== 'ALL') {
    query.set('ignored', params.ignored === 'YES' ? 'true' : 'false');
  }

  const response = await api(`/api/work-lists/${workListId}${query.toString() ? `?${query.toString()}` : ''}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke hente arbejdsliste');
  }
  return {
    work_list: (data as any).work_list as WorkListSummary,
    items: Array.isArray((data as any).items) ? ((data as any).items as WorkListItem[]) : [],
    total: Number((data as any).total || 0),
    page: Number((data as any).page || 1),
    limit: Number((data as any).limit || 50)
  };
}

export async function deleteWorkList(workListId: string): Promise<void> {
  const response = await api(`/api/work-lists/${workListId}`, {
    method: 'DELETE'
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke slette arbejdsliste');
  }
}

export async function updateWorkListItem(
  workListId: string,
  itemId: string,
  payload: Partial<Pick<WorkListItem, 'email_sendt' | 'bemaerkninger' | 'allerede_kunde' | 'ignorer'>>
): Promise<WorkListItem> {
  const response = await api(`/api/work-lists/${workListId}/items/${itemId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke opdatere item');
  }
  return (data as any).item as WorkListItem;
}

export async function updateWorkListItemEnrichment(
  workListId: string,
  itemId: string,
  payload: Partial<Pick<WorkListItem,
    'beriget_hjemmeside' | 'beriget_kontakt' | 'beriget_telefon' | 'beriget_email' | 'bemaerkninger'
  >>
): Promise<WorkListItem> {
  const response = await api(`/api/work-lists/${workListId}/items/${itemId}/enrichment`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke opdatere berigelse');
  }
  return (data as any).item as WorkListItem;
}

export async function transferWorkListItemToLead(workListId: string, itemId: string): Promise<{ lead_id: string; created: boolean }> {
  const response = await api(`/api/work-lists/${workListId}/items/${itemId}/transfer-to-lead`, {
    method: 'POST'
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke overføre til lead');
  }
  return data as { lead_id: string; created: boolean };
}

export async function refreshWorkList(workListId: string): Promise<{ inserted: number; updated: number; total: number }> {
  const response = await api(`/api/work-lists/${workListId}/refresh`, { method: 'POST' });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke opdatere arbejdsliste');
  }
  return {
    inserted: Number((data as any).inserted || 0),
    updated: Number((data as any).updated || 0),
    total: Number((data as any).total || 0)
  };
}

export async function exportWorkListExcel(workListId: string): Promise<Blob> {
  const response = await api(`/api/work-lists/${workListId}/export`);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error((data as any)?.error || 'Kunne ikke eksportere arbejdsliste');
  }
  return response.blob();
}